import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import usePageMeta from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-stack-line",
    title: "资源丰富",
    desc: "聚合电影、电视剧等多元影视类别，覆盖国产、欧美、日韩等地区内容。",
  },
  {
    icon: "ri-search-eye-line",
    title: "便捷查找",
    desc: "清晰的分类导航与关键词搜索，让你在几步之内定位想看的作品。",
  },
  {
    icon: "ri-heart-3-line",
    title: "用心推荐",
    desc: "基于类型与标签的相关推荐，帮你不断发现新的观影惊喜。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "邮箱", value: "support@blueberry-video.com", copy: true },
  { icon: "ri-wechat-line", label: "微信公众号", value: "蓝莓视频", copy: true },
  { icon: "ri-weibo-line", label: "官方微博", value: "@蓝莓视频官方", copy: false },
];

export default function About() {
  const [copied, setCopied] = useState<string | null>(null);

  usePageMeta({
    title: "关于我们 - 蓝莓视频高清影视资源",
    description:
      "了解蓝莓视频的品牌理念、服务内容与联系方式。我们专注于为用户提供丰富、便捷的高清影视资源浏览与发现体验。",
    keywords: "蓝莓视频,关于我们,影视平台,在线视频,联系方式",
  });

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopied(null);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-16">
        <section className="px-4 md:px-8 py-12 md:py-20 bg-gradient-to-b from-background-100 to-background-50 border-b border-background-200/70">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-accent-500 text-sm font-medium mb-4">
              <i className="ri-tv-2-fill"></i>
              关于蓝莓视频
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-950 mb-5">
              让每一次观影，都轻松而愉悦
            </h1>
            <p className="text-sm md:text-lg text-foreground-700 leading-relaxed">
              蓝莓视频是一个专注于高清影视资源聚合的在线内容平台。我们相信，好的影视内容应当被便捷地发现与欣赏。
              因此我们持续整理、归类海量影视资源，以清爽的门户式界面、细致的分类与贴身的相关推荐，
              帮助每一位影视爱好者快速找到心仪的作品。
            </p>
          </div>
        </section>

        <section className="px-4 md:px-8 py-12 md:py-16">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-8">
            我们的坚持
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {values.map((item) => (
              <div
                key={item.title}
                className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 mb-4">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-8 pb-12 md:pb-16">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-8">
            联系我们
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {contacts.map((item) => (
              <div
                key={item.label}
                className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6 flex flex-col gap-3"
              >
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <span className="text-sm text-foreground-500">{item.label}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm md:text-base text-foreground-900 break-all">
                    {item.value}
                  </span>
                  {item.copy && (
                    <button
                      type="button"
                      onClick={() => handleCopy(item.value)}
                      className="shrink-0 flex items-center gap-1 text-xs text-foreground-600 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={copied === item.value ? "ri-check-line" : "ri-file-copy-line"}></i>
                      {copied === item.value ? "已复制" : "复制"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-8 pb-16 md:pb-20">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              版权与免责声明
            </h2>
            <div className="space-y-3 text-sm text-foreground-600 leading-relaxed max-w-4xl">
              <p>
                本站为影视内容展示与信息聚合的演示站点，所有影视名称、简介、评分等信息仅用于页面演示，
                不构成任何形式的资源提供或观看服务。
              </p>
              <p>
                本站不提供视频文件的上传、存储、下载及真实在线播放功能，视频播放均以演示封面与状态提示呈现。
              </p>
              <p>
                我们尊重影视作品版权，如相关内容涉及权利问题，请通过上方邮箱与我们联系，我们将及时处理。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              >
                去逛电影
              </Link>
              <Link
                to="/category/tvseries"
                className="px-5 py-2.5 rounded-md bg-background-50 border border-background-200 text-foreground-900 text-sm whitespace-nowrap hover:border-primary-400 transition-colors cursor-pointer"
              >
                去逛电视剧
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}